import {Avatar, Card, ColorSchemeType, Flag} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {TestIdProps, suffixTestId} from 'shared/web';

import {SkeletonLoadingBox} from '../EntityHeader/components/SkeletonLoadingBox';

interface RecordAuditTeamMemberProps extends TestIdProps {
  name: string;
  /** Role chips: a single role (auditor / responsible person) or a participant's professions. */
  roleLabels?: string[];
  roleColorScheme: ColorSchemeType;
  /** Skeletons the flag + name while the queries resolving them are still loading. */
  isLoading?: boolean;
  /** Top-right control: edit dropdown for the auditor, delete button for participants. */
  action?: ReactNode;
}

export function RecordAuditTeamMember(props: RecordAuditTeamMemberProps) {
  const labels = props.roleLabels ?? [];

  return (
    <Card variant="inlineWhite" data-testid={props['data-testid']}>
      {/* align="flex-start" so the action (edit / delete) sits in the card's top-right corner,
          matching the header snippet edit buttons — every action icon on the page lands top-right. */}
      <HStack spacing={4} align="flex-start" justify="space-between">
        {/* minWidth 0 lets the flag/name column shrink inside a narrow grid cell — without it
            the flag's min-content width pushes the action icon outside the card. */}
        <Box flex={1} minWidth={0}>
          <HStack spacing={4} align="center">
            <Box flexShrink={0}>
              <Avatar name={props.name} size="default" />
            </Box>
            <VStack spacing={1}>
              {props.isLoading ? (
                <>
                  <SkeletonLoadingBox isLoading height={4} width={15} />
                  <SkeletonLoadingBox isLoading height={4} width={25} />
                </>
              ) : (
                <>
                  <Show when={isNotNilOrEmpty(labels)}>
                    <HStack spacing={1} wrap>
                      {labels.map((label) => (
                        <Flag
                          key={label}
                          label={label}
                          colorScheme={props.roleColorScheme}
                          isSubtle
                        />
                      ))}
                    </HStack>
                  </Show>
                  <Text
                    size="small"
                    alternative
                    color="primary"
                    data-testid={suffixTestId('name', props)}
                  >
                    {props.name}
                  </Text>
                </>
              )}
            </VStack>
          </HStack>
        </Box>
        <Show when={isNotNil(props.action)}>
          <Box flexShrink={0}>{props.action}</Box>
        </Show>
      </HStack>
    </Card>
  );
}
